/* eslint-disable no-confusing-arrow,implicit-arrow-linebreak */
import _ from 'lodash';
import { Action, Dispatch } from 'redux';
import { IAppState } from './reducers';
import {
  getLookups as getLookupsFromApi, ILookup, ILookupValue, _ILookup,
  addValuesToLookup,
  ILookupAddValue,
  removeValuesFromLookup,
} from '../api/vault/lookups';
import { SelectOptionType } from '../utils/selection';

export enum LookupName {
  ExchangeQualifier = 'exchangeQualifier',
  ExchangeSport = 'exchangeSport',
  League = 'league',
  ExchangeGameType = 'exchangeGameType',
  Team = 'team',
  Sport = 'sport',
  iptcCategory = 'photoIptcCategory',
  ExchangeVideoAngle = 'exchangeVideoAngle',
}

export enum ActionType {
  UpdateLookups = 'Lookups.UpdateLookups',
  MarkLoaded = 'Lookups.MarkLoaded',
}

export interface ILookupsAction extends Action<ActionType> {
  payload?: any;
}

export interface ILookupOption {
  value?: string;
  label: string;
  children?: {
    value: string;
    label: string;
  }[]
}

export interface ILookupOptions {
  _id: string;
  name: string;
  options: ILookupOption[];
}

export interface IState {
  isLoaded: boolean;
  lookups: ILookup[];
  lookupOptions: ILookupOptions[];
}

const INITIAL_STATE: IState = {
  isLoaded: false,
  lookups: [],
  lookupOptions: [],
};

export default function reducer(state: IState = INITIAL_STATE, action: ILookupsAction): IState {
  switch (action.type) {
    case ActionType.UpdateLookups:
      return {
        ...state,
        lookups: action.payload.lookups,
        lookupOptions: action.payload.lookupOptions,
      };
    case ActionType.MarkLoaded:
      return {
        ...state,
        isLoaded: true,
      };
    default:
      return state;
  }
}

const generateLookupsInformation = (data: Array<_ILookup>): {
  lookups: Array<ILookup>;
  lookupOptions: Array<ILookupOptions>
} => {
  const lookupOptions: Array<ILookupOptions> = [];

  if (!data) {
    return { lookups: [], lookupOptions };
  }

  const lookups: Array<ILookup> = data.reduce((newLookups, lookup) => {
    newLookups.push({
      ...lookup,
      values: lookup?.values?.map(([value]) => value),
    });

    const newLookupOption = {
      _id: lookup._id,
      name: lookup.name,
      options: [
        ...(lookup?.values ? lookup.values.map(([value]) => ({
          value: value.name,
          label: value.name,
        })) : []),
        { value: '', label: 'N/A' },
      ],
    };
    lookupOptions.push(newLookupOption);
    newLookupOption.options = _.orderBy(newLookupOption.options, 'displayOrder', 'asc');

    return newLookups;
  }, []);

  return {
    lookups,
    lookupOptions,
  };
};

export const getLookups = (vendorSport?: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const response = await getLookupsFromApi(vendorSport);
    const { lookups, lookupOptions } = generateLookupsInformation(response.data.data);

    dispatch({
      type: ActionType.UpdateLookups,
      payload: {
        lookups,
        lookupOptions,
      },
    });

    dispatch({ type: ActionType.MarkLoaded });
  } catch (e) {
    console.error('[QA] could not load lookup data', e.toString());
  }
};

// Gets a specific lookup by name and sport (optional) and updates the state
export const updateLookupByName = (name: string, sport?: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const lookupsState = getState().lookups;
    const lookups = lookupsState?.lookups ?? [];
    const lookupOptions = lookupsState?.lookupOptions ?? [];
    const response = await getLookupsFromApi(sport, name);
    const { lookups: newLookups, lookupOptions: newLookupOptions } = generateLookupsInformation(response.data.data);

    // Lookup
    const lookupIndex = lookups.findIndex((l) => l.name === name);
    const [firstNewLookup] = newLookups;
    if (lookupIndex === -1) lookups.push(firstNewLookup);
    else lookups[lookupIndex] = firstNewLookup;
    // Lookup Options
    const [firstNewLookupOption] = newLookupOptions;
    const optionIndex = lookupOptions.findIndex((l) => l.name === name);
    if (optionIndex === -1) lookupOptions.push(firstNewLookupOption);
    else lookupOptions[optionIndex] = firstNewLookupOption;

    dispatch({
      type: ActionType.UpdateLookups,
      payload: {
        lookups: [...lookups],
        lookupOptions: [...lookupOptions],
      },
    });

    dispatch({ type: ActionType.MarkLoaded });
  } catch (e) {
    console.error('[QA] could not load lookup data', e.toString());
  }
};

export const selectExchangeQualifiersOptions = (state: IAppState): Array<ILookupOption> => {
  if (state.lookups?.lookups?.length > 0) {
    const found = state.lookups.lookups.find(({ name }) => name === LookupName.ExchangeQualifier);
    if (!found?.values) return [];
    let exchangeQualifiers: ILookupValue[] = found.values;
    exchangeQualifiers = _.orderBy(exchangeQualifiers, 'displayOrder', 'asc');
    return exchangeQualifiers.filter(({ parentId }) => !parentId).map(({ _id, name, description }) => ({
      value: name,
      label: description,
      children: exchangeQualifiers.filter(({ parentId }) => (parentId) && parentId === _id).map((child) => ({
        value: child.name,
        label: child.description,
      })),
    }));
  }
  return [];
};

export const selectExchangeVideoAngleOptions = (state: IAppState): Array<ILookupOption> => selectOptionsByName(LookupName.ExchangeVideoAngle, state);

export const selectExchangeSportOptions = (state: IAppState, removeNullValue: boolean = false): Array<ILookupOption> => selectOptionsByName(LookupName.ExchangeSport, state, removeNullValue);

export const selectLeagueOptions = (state: IAppState, removeNullValue: boolean = false): Array<ILookupOption> => selectOptionsByName(LookupName.League, state, removeNullValue);

export const selectExchangeGameTypeOptions = (state: IAppState, removeNullValue: boolean = false): Array<ILookupOption> => selectOptionsByName(LookupName.ExchangeGameType, state, removeNullValue);

export const selectOptionsByName = (lookupName: LookupName, state: IAppState, removeNullValue: boolean = false): ILookupOption[] => {
  if (state?.lookups?.lookupOptions?.length > 0) {
    const foundValues = state.lookups.lookupOptions.find((lookup) => (lookup.name === lookupName));
    if (foundValues) {
      if (removeNullValue) return foundValues.options.filter((option) => option.label !== 'N/A');
      return foundValues.options;
    }
  }
  return [];
};

export const optionsToReactSelect = (options: Array<ILookupOption>) => (options || []).reduce((container, option): Array<SelectOptionType<string>> => {
  if (option?.children?.length) {
    return [...container, {
      label: option.label,
      options: option.children,
    }];
  }
  return [...container, option];
}, []);
// change addLookupValues functionality
export const addLookupValues = (lookupObjectId: string, updateLookup: ILookupAddValue[]) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const response = await addValuesToLookup(lookupObjectId, updateLookup);
    const optionId = response.data.data._id;
    const lookupsState = getState().lookups;
    const lookups = lookupsState?.lookups ?? [];
    const lookupOptions = lookupsState?.lookupOptions ?? [];
    const { lookups: tempLookups, lookupOptions: tempLookupOptions } = generateLookupsInformation([response.data.data]);
    lookups.find((l) => l._id === optionId).values = tempLookups[0].values;
    lookupOptions.find((l) => l._id === optionId).options = tempLookupOptions[0].options;
    dispatch({
      type: ActionType.UpdateLookups,
      payload: {
        lookups: [...lookups],
        lookupOptions: [...lookupOptions],
      },
    });

    dispatch({ type: ActionType.MarkLoaded });
  } catch (e) {
    console.error('[QA] could not add lookup data', e.toString());
  }
};

export const removeLookupValues = (lookupObjectId: string, names: string[]) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  try {
    const params = names.map((name) => ({ name }));
    const response = await removeValuesFromLookup(lookupObjectId, params);
    const optionId = response.data.data._id;
    const lookupsState = getState().lookups;
    const lookups = lookupsState?.lookups ?? [];
    const lookupOptions = lookupsState?.lookupOptions ?? [];
    const { lookups: tempLookups, lookupOptions: tempLookupOptions } = generateLookupsInformation([response.data.data]);
    lookups.find((l) => l._id === optionId).values = tempLookups[0].values;
    lookupOptions.find((l) => l._id === optionId).options = tempLookupOptions[0].options;
    dispatch({
      type: ActionType.UpdateLookups,
      payload: {
        lookups: [...lookups],
        lookupOptions: [...lookupOptions],
      },
    });

    dispatch({ type: ActionType.MarkLoaded });
  } catch (e) {
    console.error('[QA] could not remove lookup data', e.toString());
  }
};

export const selectLookupOptions = (lookupId: string, lookups: IState): ILookupOption[] => {
  if (lookups?.lookupOptions?.length > 0) {
    const foundValues = lookups.lookupOptions.find((lookup) => (lookup._id === lookupId));
    if (foundValues) return foundValues.options;
  }
  return [];
};
